import { useForm } from 'react-hook-form';
import { useState, useEffect } from 'react';

import Card from '@mui/material/Card';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';

import { fDateTime } from 'src/utils/format-time';

import {
  useGetRfmAutomationQuery,
  useUpdateRfmAutomationMutation,
  type UpdateRfmAutomationMutationVariables,
} from 'src/graphql/generated';

import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFSwitch } from 'src/components/hook-form';

export default function RfmAutomationSettings() {
  const { data, loading, error, refetch, updateQuery } = useGetRfmAutomationQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const [saveAutomation] = useUpdateRfmAutomationMutation();
  const [saveError, setSaveError] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const methods = useForm<UpdateRfmAutomationMutationVariables>({
    defaultValues: { dailyRefreshEnabled: false },
  });
  const {
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting },
  } = methods;
  const persistedEnabled = data?.getRfmAutomation?.dailyRefreshEnabled;
  const lastCompletedAt = data?.getRfmAutomation?.lastCompletedAt;
  const hasSettings = typeof persistedEnabled === 'boolean';
  const savedStatus = persistedEnabled
    ? 'Ativa. As faixas e as categorias serão recalculadas na próxima execução diária.'
    : 'Desativada. As faixas ficam fixas; novas compras e alterações manuais continuam atualizando a classificação.';

  useEffect(() => {
    if (typeof persistedEnabled === 'boolean') {
      reset({ dailyRefreshEnabled: persistedEnabled });
    }
  }, [persistedEnabled, reset]);

  const onSubmit = handleSubmit(async ({ dailyRefreshEnabled }) => {
    if (!hasSettings) return;
    setSaveError(false);
    try {
      const result = await saveAutomation({ variables: { dailyRefreshEnabled } });
      const savedAutomation = result.data?.saveRfmAutomation;
      const savedEnabled = savedAutomation?.dailyRefreshEnabled;
      if (typeof savedEnabled !== 'boolean') throw new Error('Missing saved RFM preference');

      updateQuery((previous) => ({
        ...previous,
        getRfmAutomation: {
          ...previous.getRfmAutomation,
          dailyRefreshEnabled: savedEnabled,
          lastCompletedAt: savedAutomation?.lastCompletedAt ?? null,
        },
      }));
      reset({ dailyRefreshEnabled: savedEnabled });
      enqueueSnackbar(
        savedEnabled ? 'Atualização diária ativada.' : 'Atualização diária desativada.',
        { variant: 'success' }
      );
    } catch {
      setSaveError(true);
      reset({ dailyRefreshEnabled: persistedEnabled });
      // Reconcile the persisted value if the server saved before a network failure.
      await refetch().catch(() => undefined);
    }
  });

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Stack component={Card} spacing={2} sx={{ p: 3 }}>
        <Stack spacing={0.5}>
          <Typography variant="h6" component="h2">
            Atualização da RFM
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Quando ativada, ajusta as faixas de recência, frequência e valor monetário conforme as
            compras da sua loja e atualiza os indicadores e as categorias dos clientes todos os dias.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Enquanto estiver ativa, as faixas editadas manualmente também serão recalculadas.
          </Typography>
        </Stack>

        {loading && (
          <Skeleton variant="rounded" height={80} aria-label="Carregando preferência RFM" />
        )}
        {!loading && (error || !hasSettings) && (
          <Alert
            severity="error"
            action={<Button onClick={() => refetch()}>Tentar novamente</Button>}
          >
            Não foi possível carregar a preferência de atualização. Tente novamente.
          </Alert>
        )}
        {!loading && !error && hasSettings && (
          <>
            <RHFSwitch
              name="dailyRefreshEnabled"
              label="Recalcular a RFM automaticamente todos os dias"
              disabled={isSubmitting}
            />
            <Typography variant="body2" color="text.secondary" role="status">
              {isDirty ? 'Alteração não salva. Clique em Salvar para confirmar.' : savedStatus}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {lastCompletedAt
                ? `Última atualização concluída em ${fDateTime(
                    lastCompletedAt,
                    "dd/MM/yyyy 'às' HH:mm"
                  )}.`
                : 'Ainda não há registro de atualização concluída.'}
            </Typography>
            {saveError && (
              <Alert severity="error">
                Não foi possível confirmar a alteração. Confira a configuração e tente novamente.
              </Alert>
            )}
            {isDirty && (
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  disabled={isSubmitting}
                  onClick={() => {
                    reset({ dailyRefreshEnabled: persistedEnabled });
                    setSaveError(false);
                  }}
                >
                  Cancelar
                </Button>
                <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                  Salvar
                </LoadingButton>
              </Stack>
            )}
          </>
        )}
      </Stack>
    </FormProvider>
  );
}
